import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Icon, Loader, Popup } from 'semantic-ui-react';
import sendToast from '../../../utils/Toast';
import { Post } from '../../types/Post';
import { listPosts, getPostUploadUrl, createPost, updatePost, deletePost, likePost, checkHasPosted } from '../../services/Posts';
import PostCard from './PostCard';
import PostFormModal, { MAX_CHARS } from './PostFormModal';
import { ALLOWED_TYPES, MAX_FILE_SIZE } from '../../utils/fileValidation';
import PostAcknowledgmentModal from './PostAcknowledgmentModal';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import './style.less';
import { normalizeId } from '../../../utils/normalizeId';
import useResidentIdentities from '../../hooks/useResidentIdentities';
import Avatar from '../../../components/components/Avatar';

interface PostsFeedProps {
    facilityId: string;
    currentResidentId: string;
    groupResidentIds?: string[];
    groupStaffIds?: string[];
    selectedGroupIds?: string[];
    onMentionClick?: (type: 'resident' | 'staff', id: string) => void;
    composerFirstName?: string;
    composerLastName?: string;
    composerImage?: string;
    groupNameById?: Record<string, string>;
    groupsLoading?: boolean;
    // Both tabs stay mounted (see resident-social/index.tsx), so this tells the
    // fetch effect whether it's the one actually visible right now.
    isActive: boolean;
}

const PAGE_SIZE = 20;

// Curated rather than a full emoji library — this app's audience skews toward
// residents who benefit from a short, large-target list over a searchable grid
// of thousands.
const EMOJI_OPTIONS = [
    '😊', '😄', '😁', '🙂', '😉', '😍',
    '🥰', '😘', '🤗', '🤩', '😎', '🤔',
    '😂', '😢', '😮', '🥺', '😴', '🤒',
    '👍', '👏', '🙌', '🙏', '💪', '👋',
    '❤️', '💕', '💖', '💐', '🌹', '🌸',
    '🌻', '🌈', '☀️', '⭐', '🎉', '🎂',
    '🎈', '🎁', '🥳', '🍰', '☕', '🍎',
];

const PostsFeed = ({ facilityId, currentResidentId, groupResidentIds, groupStaffIds, selectedGroupIds, onMentionClick, composerFirstName, composerLastName, composerImage, groupNameById, groupsLoading = false, isActive }: PostsFeedProps) => {
    const [posts, setPosts] = useState<Post[]>([]);
    const [totRecords, setTotRecords] = useState(0);
    const [pageNo, setPageNo] = useState(1);
    // True until the first fetch settles. The effect that starts it runs after
    // paint, so beginning false flashes the empty state for a frame.
    const [isLoading, setIsLoading] = useState(true);
    const [hasMoreByPage, setHasMoreByPage] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showFormModal, setShowFormModal] = useState(false);
    const [editingPost, setEditingPost] = useState<Post | null>(null);
    // handleCreateOrEdit's closure can be stale relative to setEditingPost (state
    // updates are async), so it reads this ref instead — updated synchronously
    // alongside the state, in the same tick a caller decides create vs. edit.
    const editingPostRef = useRef<Post | null>(null);
    const updateEditingPost = (post: Post | null) => {
        editingPostRef.current = post;
        setEditingPost(post);
    };
    // isSubmitting drives UI disabling, but as state it's not visible to a second
    // call fired in the same tick (e.g. a rapid double-click before re-render).
    // This ref is the actual re-entrancy guard, checked synchronously.
    const submittingRef = useRef(false);
    const [deletingPost, setDeletingPost] = useState<Post | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    // Same synchronous re-entrancy guard as submittingRef, for the same reason:
    // isDeleting as state isn't visible to a second call fired in the same tick.
    const deletingRef = useRef(false);

    const [showAckModal, setShowAckModal] = useState(false);
    const [checkingFirstPost, setCheckingFirstPost] = useState(false);

    // Inline composer draft. Posts are created directly from this card now, no
    // confirmation modal — the modal is only reused for editing an existing post.
    const [draftContent, setDraftContent] = useState('');
    const [draftImageFile, setDraftImageFile] = useState<File | null>(null);
    const [draftImagePreview, setDraftImagePreview] = useState('');
    const [draftImageError, setDraftImageError] = useState('');
    const [draftGroupIds, setDraftGroupIds] = useState<string[]>([]);
    // Tracks whether the user has manually touched the group picker for the
    // current draft, so an unrelated sidebar/context change doesn't clobber it.
    const groupsTouchedRef = useRef(false);
    const draftFileInputRef = useRef<HTMLInputElement>(null);
    const draftTextareaRef = useRef<HTMLTextAreaElement>(null);

    const selectedGroupIdSet = useMemo(
        () => new Set((selectedGroupIds || []).map(normalizeId).filter(Boolean)),
        [selectedGroupIds],
    );
    const groupResidentIdSet = useMemo(
        () => new Set((groupResidentIds || []).map(normalizeId).filter(Boolean)),
        [groupResidentIds],
    );
    const groupStaffIdSet = useMemo(
        () => new Set((groupStaffIds || []).map(normalizeId).filter(Boolean)),
        [groupStaffIds],
    );

    // Authors and likers are stored denormalized on the post, so join the loaded
    // resident ids back to the directory to pick up their social profile identity.
    const residentIdsInPosts = useMemo(() => {
        const ids = new Set<string>();
        // Include the viewer so post cards can resolve them in the same lookup.
        const viewerId = normalizeId(currentResidentId);
        if (viewerId) ids.add(viewerId);
        posts.forEach(post => {
            if (post.AuthorType === 'resident') {
                const authorId = normalizeId(post.AuthorId);
                if (authorId) ids.add(authorId);
            }
            (post.likers || []).forEach(liker => {
                if (liker.LikerType !== 'resident') return;
                const likerId = normalizeId(liker.LikerId);
                if (likerId) ids.add(likerId);
            });
        });
        return Array.from(ids);
    }, [posts, currentResidentId]);
    const residentIdentityById = useResidentIdentities(facilityId, residentIdsInPosts);

    // The logged-in resident's own name and photo come from the local helper (passed in by the page), so the
    // composer avatar, name and placeholder always agree. The directory lookup only fills a missing value.
    const viewerIdentity = residentIdentityById[normalizeId(currentResidentId)];
    const composeAvatarImage = composerImage || viewerIdentity?.Image;
    const composeFirstName = composerFirstName || viewerIdentity?.FirstName;
    const composeLastName = composerLastName || viewerIdentity?.LastName;

    const applyClientSideGroupFilter = (items: Post[]): Post[] => {
        if (!selectedGroupIdSet.size) return items;

        return items.filter((post) => {
            const postGroupIds = (post.Groups || []).map(normalizeId).filter(Boolean);
            const postAuthorId = normalizeId(post.AuthorId);
            const matchesSelectedGroup = postGroupIds.some((groupId) => selectedGroupIdSet.has(groupId));
            const matchesResidentGroupMember = post.AuthorType === 'resident' && groupResidentIdSet.has(postAuthorId);
            const matchesStaffGroupMember = post.AuthorType === 'staff' && groupStaffIdSet.has(postAuthorId);

            return matchesSelectedGroup || matchesResidentGroupMember || matchesStaffGroupMember;
        });
    };

    const fetchPosts = async (page: number, reset = false) => {
        try {
            // Set loading before clearing, so a reset never paints the empty state.
            setIsLoading(true);
            if (reset) {
                setPosts([]);
                setHasMoreByPage(true);
            }
            const response = await listPosts({
                facilityId,
                callerResidentId: currentResidentId,
                ResidentIds: groupResidentIds,
                StaffIds: groupStaffIds,
                GroupIds: selectedGroupIds,
                pageNo: page,
                pageSize: PAGE_SIZE,
            });
            const serverPosts = response.Result || [];
            const fetchedPosts = applyClientSideGroupFilter(serverPosts);
            setHasMoreByPage(serverPosts.length === PAGE_SIZE);
            if (reset) {
                setPosts(fetchedPosts);
            } else {
                setPosts(prev => [...prev, ...fetchedPosts]);
            }
            setTotRecords(response.TotRecords || 0);
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Error fetching posts');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Skip while this tab is hidden — refetches when isActive flips back to
        // true, so filter changes made while hidden are picked up on return.
        if (!isActive) return;
        setPageNo(1);
        fetchPosts(1, true);
        // The sidebar's selected group is the default target for new posts; follow
        // it when the group context changes, but don't clobber a selection the
        // user already made by hand in the composer's group picker.
        if (!groupsTouchedRef.current) {
            setDraftGroupIds(selectedGroupIds || []);
        }
    }, [isActive, facilityId, currentResidentId, groupResidentIds, groupStaffIds, selectedGroupIds]);

    // Modals here render via a React portal to document.body, so the wrapping
    // display:none this tab is hidden with (resident-social/index.tsx) doesn't
    // hide them — close them explicitly instead of leaving them orphaned on top
    // of the other tab.
    useEffect(() => {
        if (isActive) return;
        setShowFormModal(false);
        updateEditingPost(null);
        setShowAckModal(false);
        setDeletingPost(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]);

    const groupOptions = useMemo(
        () => Object.entries(groupNameById || {}).map(([id, name]) => ({ key: id, value: id, text: name })),
        [groupNameById],
    );

    const handleLoadMore = async () => {
        if (isLoading || !showLoadMore) return;
        const nextPage = pageNo + 1;
        setPageNo(nextPage);
        await fetchPosts(nextPage);
    };

    // Revoke the last preview URL when the component goes away.
    useEffect(() => {
        return () => {
            if (draftImagePreview.startsWith('blob:')) URL.revokeObjectURL(draftImagePreview);
        };
    }, [draftImagePreview]);

    const autoGrowDraft = (el: HTMLTextAreaElement | null) => {
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    };

    const handleDraftChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDraftContent(e.target.value);
        autoGrowDraft(e.target);
    };

    // Inserts at the cursor rather than appending, so picking an emoji mid-sentence
    // doesn't jump it to the end of what's already been typed.
    const insertEmoji = (emoji: string) => {
        const el = draftTextareaRef.current;
        const start = el?.selectionStart ?? draftContent.length;
        const end = el?.selectionEnd ?? draftContent.length;
        const nextContent = draftContent.slice(0, start) + emoji + draftContent.slice(end);
        if (nextContent.length > MAX_CHARS) {
            sendToast('error', `Post can't be longer than ${MAX_CHARS} characters`);
            return;
        }

        setDraftContent(nextContent);
        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            const cursorPos = start + emoji.length;
            el.setSelectionRange(cursorPos, cursorPos);
            autoGrowDraft(el);
        });
    };

    const handleDraftImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_TYPES.includes(file.type)) {
            setDraftImageError('Only JPEG, PNG, GIF, and WebP images are allowed');
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setDraftImageError('Image must be under 5MB');
            return;
        }

        setDraftImageError('');
        setDraftImageFile(file);
        setDraftImagePreview(prev => {
            if (prev.startsWith('blob:')) URL.revokeObjectURL(prev);
            return URL.createObjectURL(file);
        });
    };

    const clearDraftImage = () => {
        setDraftImageFile(null);
        setDraftImagePreview(prev => {
            if (prev.startsWith('blob:')) URL.revokeObjectURL(prev);
            return '';
        });
        setDraftImageError('');
        if (draftFileInputRef.current) draftFileInputRef.current.value = '';
    };

    const clearDraft = () => {
        setDraftContent('');
        clearDraftImage();
        setDraftGroupIds(selectedGroupIds || []);
        groupsTouchedRef.current = false;
        if (draftTextareaRef.current) draftTextareaRef.current.style.height = 'auto';
    };

    const uploadImage = async (file: File): Promise<{ fileName: string; imageUrl: string }> => {
        const uploadData = await getPostUploadUrl({ facilityId, fileName: file.name });
        const uploadRes = await fetch(uploadData.presignedUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
        });
        // fetch only rejects on network failure, so an expired or denied presign resolves
        // fine — the response was never even captured, and createPost/updatePost went on to
        // save an ImageUrl for an object that was never stored. Same check the comment
        // upload path uses.
        if (!uploadRes.ok) {
            throw new Error('Failed to upload image. Please try again.');
        }
        return { fileName: uploadData.fileName, imageUrl: uploadData.imageUrl };
    };

    const handleCreateOrEdit = async (content: string, imageFile: File | null, removeImage: boolean, groups: string[]) => {
        // Captured once, from the ref (not the closed-over `editingPost` state) so
        // a same-tick updateEditingPost(null) — e.g. the inline composer clearing
        // edit mode right before posting — is respected even though this closure
        // may have been created while the Edit modal was still open.
        const activeEditingPost = editingPostRef.current;
        if (submittingRef.current) return;
        submittingRef.current = true;
        try {
            setIsSubmitting(true);
            let imageFileName: string | undefined;
            let imageUrl: string | undefined;

            if (imageFile) {
                const uploaded = await uploadImage(imageFile);
                imageFileName = uploaded.fileName;
                imageUrl = uploaded.imageUrl;
            } else if (removeImage && activeEditingPost) {
                imageFileName = '';
                imageUrl = '';
            }

            if (activeEditingPost) {
                await updatePost({
                    facilityId,
                    registrantId: currentResidentId,
                    postId: activeEditingPost._id,
                    content,
                    imageFileName,
                    imageUrl,
                    groups,
                });
                sendToast('success', 'Post updated');
            } else {
                const result = await createPost({
                    facilityId,
                    registrantId: currentResidentId,
                    content,
                    imageFileName,
                    imageUrl,
                    groups,
                });
                if (result?.ApprovalStatus === 'pending') {
                    sendToast('success', 'Your post has been submitted for approval');
                } else {
                    sendToast('success', 'Post created');
                }
            }

            setShowFormModal(false);
            if (!activeEditingPost) clearDraft();
            updateEditingPost(null);
            setPageNo(1);
            await fetchPosts(1, true);
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Error saving post');
        } finally {
            setIsSubmitting(false);
            submittingRef.current = false;
        }
    };

    const handleEdit = (post: Post) => {
        updateEditingPost(post);
        setShowFormModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deletingPost || deletingRef.current) return;
        deletingRef.current = true;
        try {
            setIsDeleting(true);
            await deletePost({
                facilityId,
                registrantId: currentResidentId,
                postId: deletingPost._id,
            });
            setPosts(prev => prev.filter(p => p._id !== deletingPost._id));
            // Floored: totRecords is the server's count for the current filter, and a
            // delete can race a refetch that already reflected it. An unfloored decrement
            // drove it negative, which then made showLoadMore false forever.
            setTotRecords(prev => Math.max(prev - 1, 0));
            sendToast('success', 'Post deleted');
            setDeletingPost(null);
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Error deleting post');
        } finally {
            setIsDeleting(false);
            deletingRef.current = false;
        }
    };

    const handlePostClick = async () => {
        if (!draftContent.trim()) return;
        updateEditingPost(null);
        try {
            setCheckingFirstPost(true);
            const hasPosted = await checkHasPosted({ facilityId, callerResidentId: currentResidentId });
            if (hasPosted) {
                await handleCreateOrEdit(draftContent.trim(), draftImageFile, false, draftGroupIds);
            } else {
                setShowAckModal(true);
            }
        } catch {
            // If we can't confirm first-post status, fail closed: route through the
            // acknowledgment modal rather than silently auto-publishing.
            setShowAckModal(true);
        } finally {
            setCheckingFirstPost(false);
        }
    };

    const handleAcknowledge = async () => {
        setShowAckModal(false);
        await handleCreateOrEdit(draftContent.trim(), draftImageFile, false, draftGroupIds);
    };

    const handleToggleLike = async (post: Post) => {
        const original = post;
        const currentlyLiked = post.likedByMe ?? false;
        const currentCount = post.LikeCount ?? 0;

        // Optimistic update for snappy UI; no extra loader on button
        setPosts(prev =>
            prev.map(p =>
                p._id === post._id
                    ? {
                          ...p,
                          likedByMe: !currentlyLiked,
                          LikeCount: Math.max(0, currentCount + (currentlyLiked ? -1 : 1)),
                      }
                    : p
            )
        );

        try {
            const result = await likePost({
                facilityId,
                postId: post._id,
                registrantId: currentResidentId,
            });
            setPosts(prev =>
                prev.map(p =>
                    p._id === post._id
                        ? {
                              ...p,
                              likedByMe: result.liked,
                              LikeCount: result.likeCount,
                          }
                        : p
                )
            );
        } catch (error) {
            // Revert optimistic change on error
            setPosts(prev => prev.map(p => (p._id === original._id ? original : p)));
            sendToast('error', error instanceof Error ? error.message : 'Error updating like');
        }
    };

    const showLoadMore = selectedGroupIdSet.size > 0
        ? hasMoreByPage
        : posts.length < totRecords;

    return (
        <div className="posts-feed">
            <div className="posts-compose-card">
                <div className="compose-row">
                    <div className="compose-avatar">
                        <Avatar
                            src={composeAvatarImage}
                            firstName={composeFirstName}
                            lastName={composeLastName}
                        />
                    </div>
                    <textarea
                        ref={draftTextareaRef}
                        className="compose-input"
                        placeholder={composeFirstName
                            ? `Share something with your community, ${composeFirstName}…`
                            : 'Share something with your community…'}
                        value={draftContent}
                        onChange={handleDraftChange}
                        maxLength={MAX_CHARS}
                        rows={1}
                    />
                </div>

                {draftImagePreview && (
                    <div className="compose-image-preview">
                        <img src={draftImagePreview} alt="Selected attachment" />
                        <button
                            type="button"
                            className="compose-image-remove"
                            onClick={clearDraftImage}
                            aria-label="Remove image"
                        >
                            <Icon name="close" />
                        </button>
                    </div>
                )}

                {draftImageError && (
                    <div className="compose-image-error">{draftImageError}</div>
                )}

                <div className="compose-actions">
                    <div className="compose-tools">
                        <input
                            ref={draftFileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleDraftImageSelect}
                            style={{ display: 'none' }}
                        />
                        <button
                            type="button"
                            className={`compose-tool-btn ${draftImageFile ? 'active' : ''}`}
                            onClick={() => draftFileInputRef.current?.click()}
                            aria-label={draftImageFile ? 'Change photo' : 'Add photo'}
                        >
                            <Icon name="image outline" />
                        </button>
                        <Popup
                            on="click"
                            position="top left"
                            trigger={
                                <button
                                    type="button"
                                    className="compose-tool-btn"
                                    aria-label="Add emoji"
                                >
                                    <Icon name="smile outline" />
                                </button>
                            }
                            content={
                                <div className="compose-emoji-grid">
                                    {EMOJI_OPTIONS.map(emoji => (
                                        <button
                                            key={emoji}
                                            type="button"
                                            className="compose-emoji-option"
                                            onClick={() => insertEmoji(emoji)}
                                            aria-label={`Insert ${emoji}`}
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                            }
                        />
                        {(groupsLoading || groupOptions.length > 0) && (
                            <Dropdown
                                multiple
                                search
                                selection
                                placeholder={groupsLoading ? 'Loading' : 'Groups (optional)'}
                                options={groupsLoading ? [] : groupOptions}
                                value={draftGroupIds}
                                disabled={groupsLoading}
                                onChange={(_e, data) => {
                                    groupsTouchedRef.current = true;
                                    setDraftGroupIds((data.value as string[]) || []);
                                }}
                                className="compose-groups-dropdown"
                            />
                        )}
                    </div>
                    <div className="compose-actions-right">
                        <span className="compose-char-count">{draftContent.length}/{MAX_CHARS}</span>
                        <Button
                            className="btn-post"
                            onClick={handlePostClick}
                            loading={checkingFirstPost || isSubmitting}
                            disabled={checkingFirstPost || isSubmitting || !draftContent.trim()}
                        >
                            <Icon name="paper plane outline" /> Post
                        </Button>
                    </div>
                </div>
            </div>

            {isLoading && posts.length === 0 && (
                <div className="posts-results-loader">
                    <Loader active inline="centered" />
                </div>
            )}

            {/* Gated on showLoadMore too: with a group filter applied the client drops posts
                the server returned, so a page can leave the list empty while later pages are
                still waiting. Without this the empty state sat right above a Load More button. */}
            {!isLoading && posts.length === 0 && !showLoadMore && (
                <div className="posts-empty-state">
                    <Icon name="comments outline" size="huge" />
                    <p>No posts yet. Be the first to share!</p>
                    <Button primary onClick={() => draftTextareaRef.current?.focus()}>Create Post</Button>
                </div>
            )}

            <div className="posts-list">
                {posts.map(post => (
                    <PostCard
                        key={post._id}
                        post={post}
                        facilityId={facilityId}
                        currentResidentId={currentResidentId}
                        onEdit={handleEdit}
                        onDelete={setDeletingPost}
                        onToggleLike={handleToggleLike}
                        onPostReported={(reported) => {
                            setPosts(prev => prev.filter(p => p._id !== reported._id));
                            setTotRecords(prev => Math.max(prev - 1, 0));
                        }}
                        onMentionClick={onMentionClick}
                        groupNameById={groupNameById}
                        residentIdentityById={residentIdentityById}
                        selectedGroupIds={selectedGroupIds}
                    />
                ))}
            </div>

            {showLoadMore && (
                <div className="load-more-button">
                    <Button onClick={handleLoadMore} loading={isLoading} disabled={isLoading}>
                        Load More
                    </Button>
                </div>
            )}

            <PostFormModal
                open={showFormModal}
                onClose={() => { setShowFormModal(false); updateEditingPost(null); }}
                onSubmit={handleCreateOrEdit}
                initialContent={editingPost?.Content || ''}
                initialImageUrl={editingPost?.ImageUrl || ''}
                initialGroupIds={editingPost?.Groups || []}
                facilityId={facilityId}
                currentResidentId={currentResidentId}
                isEdit={!!editingPost}
                isSubmitting={isSubmitting}
            />

            <PostAcknowledgmentModal
                open={showAckModal}
                onAcknowledge={handleAcknowledge}
                onClose={() => setShowAckModal(false)}
                isSubmitting={isSubmitting}
            />

            <DeleteConfirmationModal
                open={!!deletingPost}
                onClose={() => setDeletingPost(null)}
                onConfirm={handleDeleteConfirm}
                messageToDisplay="Are you sure you want to delete this post? This cannot be undone."
                loading={isDeleting}
            />
        </div>
    );
};

export default PostsFeed;
